import Vue from "vue"

import { prepMessage } from "./helpers.js"

export default {
  storeAllSessions(state, chatSessions) {
    state.sessions = chatSessions
  },
  storeMessages(state, payload) {
    const messages = Array.isArray(payload) ? payload : payload.messages
    const prepped = !Array.isArray(payload) && payload.prepped
    if (messages) {
      state.messages.all = prepped ? messages : messages.map((m) => prepMessage(Object.assign({}, m)))
    }
  },
  appendSession(state, chatSession) {
    Vue.set(state, "sessions", [...state.sessions, chatSession])
  },
  removeSession(state, sessionId) {
    const id = typeof sessionId === "number" ? sessionId : parseInt(sessionId, 10)
    state.sessions = state.sessions.filter((s) => Number(s.id) !== id)
  },
  updateSessionInList(state, chatSession) {
    state.sessions = state.sessions.map((s) => (s.id === chatSession.id ? chatSession : s))
    if (state.currentSession && state.currentSession.id === chatSession.id) {
      state.currentSession = Object.assign({}, state.currentSession, chatSession)
    }
  },
  storeSession(state, chatSession) {
    if (state.currentSession && state.currentSession.id !== chatSession.id) {
      state.messageQueue = []
    }
    if (chatSession.messages) {
      chatSession.messages = chatSession.messages.map((message) => {
        return message.__prepped ? message : prepMessage(Object.assign({}, message))
      })
    }

    state.currentSession = chatSession

    state.sessions = state.sessions.map((s) => {
      if (s.id === chatSession.id) return chatSession
      else {
        return s
      }
    })
  },
  clearCurrentSession(state) {
    state.currentSession = {}
    state.messageQueue = []
  },
  enqueueMessage(state, text) {
    if (!state.messageQueue) state.messageQueue = []
    state.messageQueue.push(text)
  },
  removeQueuedMessage(state, index) {
    if (!state.messageQueue || index < 0 || index >= state.messageQueue.length) return
    state.messageQueue.splice(index, 1)
  },
  setSendingFromQueue(state, value) {
    state.sendingFromQueue = value
  },
  setChatEnabled(state, chatEnabled) {
    state.chatEnabled = chatEnabled
  },
  loadingStarted(state, key) {
    state.loading[key] = true
  },
  loadingEnded(state, key) {
    state.loading[key] = false
  },
  setBotCreatingMessage(state, value) {
    state.systemCreating = value
  },
  setUserCreatingMessage(state, value) {
    state.userCreating = value
  },
  addMessage(state, message) {
    const updatedMessage = message.__prepped ? message : prepMessage(message)
    if (state.messages.all.some((m) => m.id === updatedMessage.id)) {
      return
    }
    state.messages.all.push(updatedMessage)
  },
  addPendingMessage(state, message) {
    if (!state.messages.all) state.messages.all = []
    state.messages.all.push(message)
  },
  confirmPendingMessage(state, { clientId, message }) {
    const updatedMessage = message.__prepped ? message : prepMessage(Object.assign({}, message))
    updatedMessage.__pending = false
    delete updatedMessage.clientId
    const idx = state.messages.all.findIndex(
      (m) => m.__pending && (m.clientId === clientId || m.id === `pending-${clientId}`)
    )
    if (idx >= 0) {
      Vue.set(state.messages.all, idx, updatedMessage)
      return
    }
    const fallbackIdx = state.messages.all.findIndex(
      (m) =>
        m.__pending &&
        m.author &&
        !m.author.isBot &&
        m.text &&
        updatedMessage.text &&
        m.text.trim() === updatedMessage.text.trim()
    )
    if (fallbackIdx >= 0) {
      Vue.set(state.messages.all, fallbackIdx, updatedMessage)
      return
    }
    if (!state.messages.all.some((m) => m.id === updatedMessage.id)) {
      state.messages.all.push(updatedMessage)
    }
  },
  removePendingMessage(state, clientId) {
    state.messages.all = (state.messages.all || []).filter(
      (m) => !(m.__pending && (m.clientId === clientId || m.id === `pending-${clientId}`))
    )
  },
  updateMessage(state, message) {
    try {
      const updatedMessage = message.__prepped ? message : prepMessage(Object.assign({}, message))
      if (!state.messages.all) return
      const idx = state.messages.all.findIndex((m) => m.id === updatedMessage.id)
      if (idx >= 0) {
        Vue.set(state.messages.all, idx, updatedMessage)
      }
    } catch (e) {
      console.log("Error updating message", e)
    }
  },
  deleteMessage(state, message) {
    state.messages.all = state.messages.all.filter((m) => m.id !== message.id)
  },
  /**
   * Merge refetched messages into state without replacing unchanged messages.
   * Preserves object identity for messages that are unchanged so the UI does not repaint them.
   */
  mergeMessages(state, payload) {
    const incoming = payload.messages || []
    if (incoming.length === 0) return
    const current = state.messages.all || []
    const pendingMessages = current.filter((m) => m.__pending)
    const byId = {}
    current.forEach((m) => { byId[m.id] = m })
    const result = []
    for (const refetched of incoming) {
      const existing = byId[refetched.id]
      if (existing && messageContentUnchanged(existing, refetched)) {
        result.push(existing)
      } else {
        result.push(refetched)
      }
    }
    for (let i = 0; i < pendingMessages.length; i++) {
      const p = pendingMessages[i]
      const hasReal = result.some(
        (r) =>
          r.author &&
          !r.author.isBot &&
          r.text &&
          p.text &&
          r.text.trim() === p.text.trim()
      )
      if (!hasReal) {
        result.push(p)
      }
    }
    state.messages.all = result
  },
}

function messageContentUnchanged (a, b) {
  if (a.id !== b.id) return false
  if (a.text !== b.text) return false
  const aHistory = a.history && Array.isArray(a.history) ? JSON.stringify(a.history) : ""
  const bHistory = b.history && Array.isArray(b.history) ? JSON.stringify(b.history) : ""
  return aHistory === bHistory
}
